// covered-by: frontends/carrie/src/ask-v2/routes/__tests__/settingsRoutes.test.ts

import { z } from "zod";

import { KnowledgeRuleCategory } from "../../../../utils/parsers/knowledge";

/**
 * Where this page lives, as a LINK target.
 *
 * Both surfaces mount it here -- `knowledgeDatabaseRoute` under the legacy tree
 * and `askV2KnowledgeDatabaseRoute` under ask-v2 -- but only the tree that is
 * actually mounted has its route objects initialised. Linking with
 * `someRoute.to` therefore resolves to `undefined` on the OTHER surface, and a
 * TanStack `Link` with no `to` does not navigate: it stays on the current route
 * and merges the search params in. That is exactly what a citation link did on
 * ask-v2 -- it appended `?tab=rules&ruleId=...` to the review URL and went
 * nowhere. Link with this path, never with a route object.
 */
export const KNOWLEDGE_DATABASE_PATH = "/settings/knowledge-database" as const;

export const MEMORIES_TAB = "memories";

export const KNOWLEDGE_TABS = [
  "sources",
  "rules",
  MEMORIES_TAB,
  "activity",
] as const;

export type KnowledgeTab = (typeof KNOWLEDGE_TABS)[number];

export const RULE_STATUS_FILTERS = [
  "active",
  "suggestions",
  "archived",
] as const;

export type RuleStatusFilter = (typeof RULE_STATUS_FILTERS)[number];

/**
 * The Knowledge Database's URL state, shared by BOTH surfaces.
 *
 * `ask-v2` mounts its own route for this page (`askV2KnowledgeDatabaseRoute`)
 * and used to hand-copy these fields. They drifted the moment one gained a
 * param: `cite` was added here and silently stripped there, so a citation deep
 * link worked on the legacy surface and did nothing on the one people use.
 * One schema, both routes, and that cannot happen again.
 *
 * `.catch(...)` (not strict enums) so stale legacy deep links — the old
 * dynamic per-model tabs used `?tab=<modelId>&pageSize=100` — land on Sources
 * instead of 404ing.
 */
export const knowledgeDatabaseSearchSchema = z.object({
  tab: z.enum(KNOWLEDGE_TABS).catch("sources"),
  status: z.enum(RULE_STATUS_FILTERS).catch("active"),
  // Single-category filter for the Rules tab; omitted = all categories.
  category: z.nativeEnum(KnowledgeRuleCategory).optional().catch(undefined),
  // Coerced for the same reason as `cite` below: the memories citation link
  // sends `q=<memory label>` verbatim, so a wholly numeric label ("2026")
  // parses as a NUMBER and a plain `z.string()` would drop it silently into
  // the unfiltered list. A label that merely STARTS with a digit is not valid
  // JSON and arrives as the raw string, so only the all-digits case is hit.
  q: z.coerce.string().optional().catch(undefined),
  ruleId: z.string().optional().catch(undefined),
  sourceId: z.string().optional().catch(undefined),
  // The cited cell range (A1 style, e.g. "A10:J10"), so a citation
  // elsewhere in the product can open THIS source at the row the claim was
  // read from rather than at the top of the sheet. `range` and not `row`:
  // rembrandt hardcodes `row: None` on every writer, and `range` is what
  // SpreadsheetView resolves a highlight from.
  //
  // Coerced, not `z.string()`: this value is EITHER a cell range ("A10:J10")
  // OR a bare page number ("19"), and the router parses search values with
  // JSON.parse. A hand-built `cite=19` therefore arrives as the NUMBER 19, a
  // plain `z.string()` rejects it, and `.catch(undefined)` swallowed the
  // rejection silently -- every PDF-page citation deep link opened the rule
  // but not the cited page, with nothing logged. `citationAnchorParam`
  // stringifies the page on the way out, so coerce on the way in.
  cite: z.coerce.string().optional().catch(undefined),
});

/**
 * Canonical landing search for cross-route links into the Knowledge Database.
 * `tab`/`status` use `.catch(...)` so their OUTPUT type is required — a bare
 * `{ tab: "rules" }` literal fails the router's Link typing. Every external
 * Link/Navigate into this route should spread this object.
 *
 * why here and not in `route.ts`: that module calls `createRoute({...})` at
 * module scope and imports `./view`, so it is not a leaf -- anything importing
 * this constant from there evaluates the whole Knowledge Database page and its
 * route registration. `Markdown.tsx` renders every chat message and review
 * comment in the app, which made that a real cost. `createRoute` is a
 * top-level side effect, so tree-shaking cannot drop it. `route.ts` re-exports
 * this name, so existing importers (and the test mocks that stub that module)
 * are unaffected.
 */
export const KNOWLEDGE_DATABASE_DEFAULT_SEARCH = {
  tab: "sources",
  status: "active",
} as const satisfies { tab: KnowledgeTab; status: RuleStatusFilter };
